import type { IntervalStackedDataPoint } from "@/services/interval"
import { DateTime, Duration, type Interval } from "luxon"

import { formatIntervalBucketLabel } from "./format-interval-bucket-label"
import {
  INTERVAL_CHART_MAX_DATA_POINTS,
  type IntervalGranularity,
  siteMidnightUtcIso,
} from "./interval-data-selection"

type CalendarUnit = Exclude<IntervalGranularity, "as_given">

const bucketKey = (
  granularity: IntervalGranularity,
  iso: string,
  timeZone: string
): string => formatIntervalBucketLabel(granularity, iso, timeZone)

interface CalendarBucketSpan {
  first: DateTime
  last: DateTime
}

const observedBucketRange = (
  data: IntervalStackedDataPoint[],
  unit: CalendarUnit,
  timeZone: string
): CalendarBucketSpan | null => {
  let first: DateTime | undefined
  let last: DateTime | undefined
  for (const point of data) {
    const bucket = DateTime.fromISO(point.xaxis, { zone: timeZone }).startOf(
      unit
    )
    if (!bucket.isValid) {
      continue
    }
    if (first == null || bucket < first) {
      first = bucket
    }
    if (last == null || bucket > last) {
      last = bucket
    }
  }
  if (first == null || last == null || first >= last) {
    return null
  }
  return { first, last }
}

const clampBucketRange = (
  range: CalendarBucketSpan,
  dateRange: Interval,
  unit: CalendarUnit,
  timeZone: string
): CalendarBucketSpan | null => {
  const startDate = dateRange.start?.toISODate()
  const endDate = dateRange.end?.toISODate()
  if (!startDate || !endDate) {
    return range
  }

  const rangeStart = DateTime.fromISO(startDate, { zone: timeZone }).startOf(
    unit
  )
  const rangeEndExclusive = DateTime.fromISO(endDate, {
    zone: timeZone,
  }).startOf("day")
  if (!rangeStart.isValid || !rangeEndExclusive.isValid) {
    return range
  }

  const rangeLast = rangeEndExclusive.minus({ days: 1 }).startOf(unit)
  const first = range.first < rangeStart ? rangeStart : range.first
  const last = range.last > rangeLast ? rangeLast : range.last
  if (first >= last) {
    return null
  }
  return { first, last }
}

const fillCalendarGaps = (
  data: IntervalStackedDataPoint[],
  dateRange: Interval,
  granularity: CalendarUnit,
  timeZone: string
): IntervalStackedDataPoint[] => {
  const observed = observedBucketRange(data, granularity, timeZone)
  if (observed == null) {
    return data
  }
  const span = clampBucketRange(observed, dateRange, granularity, timeZone)
  if (span == null) {
    return data
  }

  const byKey = new Map<string, IntervalStackedDataPoint>()
  for (const point of data) {
    byKey.set(bucketKey(granularity, point.xaxis, timeZone), point)
  }

  const filled: IntervalStackedDataPoint[] = []
  const usedKeys = new Set<string>()
  for (
    let cursor = span.first;
    cursor <= span.last;
    cursor = cursor.plus({ [granularity]: 1 })
  ) {
    const iso = siteMidnightUtcIso(cursor, timeZone)
    if (!iso) {
      continue
    }
    const key = bucketKey(granularity, iso, timeZone)
    const existing = byKey.get(key)
    if (existing) {
      usedKeys.add(key)
      filled.push(existing)
    } else {
      filled.push({ xaxis: iso })
    }
  }

  const extras = data.filter(
    (point) => !usedKeys.has(bucketKey(granularity, point.xaxis, timeZone))
  )
  if (extras.length === 0) {
    return filled
  }

  return [...filled, ...extras].sort((left, right) =>
    left.xaxis.localeCompare(right.xaxis)
  )
}

const fillAsGivenGaps = (
  data: IntervalStackedDataPoint[]
): IntervalStackedDataPoint[] => {
  const instants = data
    .map((point) => ({
      at: DateTime.fromISO(point.xaxis, { setZone: true }),
      point,
    }))
    .filter((item) => item.at.isValid)
    .sort((left, right) => left.at.toMillis() - right.at.toMillis())
  if (instants.length < 2) {
    return data
  }

  let minDeltaMs = Number.POSITIVE_INFINITY
  for (let index = 1; index < instants.length; index += 1) {
    const delta =
      instants[index].at.toMillis() - instants[index - 1].at.toMillis()
    if (delta > 0 && delta < minDeltaMs) {
      minDeltaMs = delta
    }
  }
  if (!Number.isFinite(minDeltaMs)) {
    return data
  }

  const first = instants.at(0)
  const last = instants.at(-1)
  if (first == null || last == null) {
    return data
  }

  const spanMs = last.at.toMillis() - first.at.toMillis()
  if (spanMs / minDeltaMs > INTERVAL_CHART_MAX_DATA_POINTS) {
    return data
  }

  const byMs = new Map<number, IntervalStackedDataPoint>()
  for (const item of instants) {
    byMs.set(item.at.toMillis(), item.point)
  }

  const filled: IntervalStackedDataPoint[] = []
  const endMs = last.at.toMillis()
  const step = Duration.fromMillis(minDeltaMs)
  for (
    let cursor = first.at;
    cursor.toMillis() <= endMs;
    cursor = cursor.plus(step)
  ) {
    const existing = byMs.get(cursor.toMillis())
    if (existing) {
      filled.push(existing)
      continue
    }
    const iso = cursor.toUTC().toISO({ suppressMilliseconds: true })
    if (!iso) {
      continue
    }
    filled.push({ xaxis: iso })
  }

  return filled
}

export const fillIntervalChartGaps = (
  data: IntervalStackedDataPoint[],
  dateRange: Interval,
  granularity: IntervalGranularity,
  timeZone: string
): IntervalStackedDataPoint[] => {
  if (data.length === 0 || !dateRange.isValid) {
    return data
  }

  if (granularity === "as_given") {
    return fillAsGivenGaps(data)
  }

  return fillCalendarGaps(data, dateRange, granularity, timeZone)
}
